import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTracking } from '../../../../react-components/tracking';

/**
 * A button that debounces clicks for a given delay to
 * prevent click spamming.
 */
export interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  /**
   * Delay in milliseconds to debounce button clicks
   * @default 500
   */
  delay?: number;
  /**
   * Whether the button is debounced
   * @default false
   */
  debounced?: boolean;
  /**
   * Variant of the button
   * @default 'contained'
   */
  variant?: 'contained' | 'destructive' | 'outlined' | 'text' | 'tonal';
  /**
   * The function to call when the button is clicked
   *
   * @param args anything that is passed to the onClick function
   * @returns void
   */
  onClick?: (args) => void;
  /**
   * Optional tracking name for DataDog tracking. If not provided, defaults to 'button'
   */
  trackingName?: string;
}

export const Button = ({
  delay = 500,
  variant = 'contained',
  disabled = false,
  debounced = false,
  startIcon = null,
  endIcon = null,
  children,
  onClick,
  trackingName,
  ...props
}: ButtonProps) => {
  const [isDebounced, setIsDebounced] = useState<boolean>(false);
  const { generateTrackingProps } = useTracking();

  /*
   * Prefer an explicit trackingName. Preserve caller-provided
   * data-dd-action-name (e.g. InfoBox) instead of overwriting it.
   */
  const incomingActionName =
    typeof props['data-dd-action-name'] === 'string'
      ? props['data-dd-action-name']
      : undefined;
  const trackingProps = incomingActionName
    ? {}
    : generateTrackingProps(trackingName || 'button');

  useEffect(() => {
    if (!isDebounced) {
      return;
    }

    const rebounce = setTimeout(() => {
      setIsDebounced(false);
    }, delay);
    return () => clearTimeout(rebounce);
  }, [isDebounced, delay, disabled]);

  function handleClick(e) {
    if (isDebounced) {
      return;
    }

    if (debounced) {
      setIsDebounced(true);
    }

    return onClick(e);
  }

  return (
    <MuiButton
      variant={variant}
      disabled={isDebounced || disabled}
      onClick={onClick ? handleClick : null}
      startIcon={startIcon}
      endIcon={endIcon}
      {...props}
      {...trackingProps}
      sx={{
        ...props.sx,
      }}
    >
      {children}
    </MuiButton>
  );
};
